import React from "react";
import {
    Box,
    Chip,
    Paper,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import StraightenOutlinedIcon from "@mui/icons-material/StraightenOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PaletteOutlinedIcon from "@mui/icons-material/PaletteOutlined";
import TitleOutlinedIcon from "@mui/icons-material/TitleOutlined";

type QuickFactItem = {
    label: string;
    value?: React.ReactNode;
    icon?: React.ReactNode;
    emphasize?: boolean;
};

type ArtworkQuickFactsPanelProps = {
    items: QuickFactItem[];
    title?: string;
};

const ArtworkQuickFactsPanel: React.FC<ArtworkQuickFactsPanelProps> = ({
    items,
    title = "Key Info",
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const visibleItems = items.filter(
        (item) => item.value !== undefined && item.value !== null && `${item.value}`.trim() !== ""
    );

    if (visibleItems.length === 0) return null;

    return (
        <Paper
            elevation={1}
            sx={{
                p: { xs: 1.5, sm: 2 },
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "background.paper",
            }}
        >
            <Stack spacing={1.5}>
                <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 700 }}
                >
                    {title}
                </Typography>

                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: {
                            xs: "1fr",
                            sm: "1fr 1fr",
                        },
                        gap: 1.25,
                    }}
                >
                    {visibleItems.map((item) => (
                        <Box
                            key={item.label}
                            sx={{
                                display: "grid",
                                gridTemplateColumns: "24px 1fr",
                                gap: 1,
                                alignItems: "start",
                                minWidth: 0,
                                p: 1,
                                borderRadius: 1.5,
                                bgcolor: item.emphasize ? "grey.50" : "transparent",
                            }}
                        >
                            <Box sx={{ color: "text.secondary", pt: 0.2 }}>
                                {item.icon}
                            </Box>

                            <Box sx={{ minWidth: 0 }}>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        display: "block",
                                        color: "text.secondary",
                                        fontWeight: 700,
                                        textTransform: "uppercase",
                                        letterSpacing: 0.3,
                                        mb: 0.25,
                                    }}
                                >
                                    {item.label}
                                </Typography>

                                <Typography
                                    variant={item.emphasize ? "body1" : "body2"}
                                    sx={{
                                        fontWeight: item.emphasize ? 600 : 400,
                                        wordBreak: "break-word",
                                    }}
                                >
                                    {item.value}
                                </Typography>
                            </Box>
                        </Box>
                    ))}
                </Box>
            </Stack>
        </Paper>
    );
};

export default ArtworkQuickFactsPanel;